import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'

/**
 * Campaign finance — what the campaign spends on itself.
 *
 * ## Not programme money
 *
 * `programs` tracks what reaches a beneficiary and `station-mates` tracks what reaches a
 * cell member on polling day. This is the third kind, which had no home until now: rally
 * venues, fuel, posters, airtime, agents' allowances — and it is the one the Election
 * Campaign Financing Act obliges a candidate to file a return on.
 */

export interface Expense {
  id: string
  reference: string
  electionId: string | null
  eventId: string | null
  eventTitle: string | null
  /** EXPENSE is positive; REVERSAL is negative and names what it reverses. */
  entryTypeCode: 'EXPENSE' | 'REVERSAL' | string
  reversesId: string | null
  reversesReference: string | null
  reversalReasonCode: string | null
  reversalNote: string | null
  /** Set when a LATER entry reverses this one, so a reader is never misled by the original. */
  reversedById: string | null
  incurredOn: string
  categoryCode: string
  categoryLabel: string
  purpose: string | null
  unitId: string | null
  unitName: string | null
  unitPath: string | null
  payee: string | null
  amount: number
  /** How many, what one cost, and what the thing was. */
  quantity: number | null
  unitAmount: number | null
  unitLabel: string | null
  currency: string
  paymentMethodCode: string
  paymentReference: string | null
  description: string | null
  mobilizerId: string | null
  receiptCount: number
  /**
   * True when a receipt was READ and its amount differs from the typed one.
   * Null when nothing was read — a different state from "they agree", and the page must
   * not render the two the same way.
   */
  receiptAmountDisputed: boolean | null
  createdAt: string
}

export interface Receipt {
  id: string
  expenseId: string
  documentNumber: string | null
  issuedOn: string | null
  supplier: string | null
  amount: number | null
  currency: string | null
  storageObjectId: string | null
  fileName: string | null
  contentType: string | null
  ocrAmount: number | null
  ocrConfidence: number | null
  ocrReadAt: string | null
  createdAt: string
}

export interface CampaignEvent {
  id: string
  code: string
  electionId: string | null
  title: string
  eventTypeCode: string
  statusCode: string
  startsAt: string
  endsAt: string | null
  unitId: string | null
  unitName: string | null
  unitPath: string | null
  venue: string | null
  expectedAttendance: number | null
  actualAttendance: number | null
  /** How the head count was arrived at — a gate count and an eye estimate are not the same. */
  attendanceBasisCode: string | null
  unitRegisteredVoters: number | null
  notes: string | null
  /** Net of reversals. */
  spend: number | null
  spendCurrency: string | null
  expenseCount: number
  namedAttendees: number
  createdAt: string
}

export interface Attendee {
  id: string
  eventId: string
  voterId: string | null
  mobilizerId: string | null
  userId: string | null
  fullName: string
  roleAtEvent: string | null
  organisation: string | null
  phoneMask: string | null
  attended: boolean
  note: string | null
}

export interface SpendSlice {
  key: string
  label: string
  amount: number | null
  entries: number
  share: number | null
}

export interface DayPoint {
  day: string
  amount: number
  cumulative: number
}

export interface SpendAnalysis {
  from: string
  to: string
  currency: string | null
  /** Set when the window holds more than one currency — every total is then suppressed. */
  mixedCurrencyReason: string | null
  gross: number | null
  reversed: number | null
  net: number | null
  entries: number
  reversals: number
  perDay: number | null
  withoutReceipt: number
  withoutReceiptAmount: number | null
  disputedReceipts: number
  byCategory: SpendSlice[]
  byUnit: SpendSlice[]
  byMethod: SpendSlice[]
  byEvent: SpendSlice[]
  daily: DayPoint[]
}

export interface Vocab {
  code: string
  label: string
}

interface Paged<T> {
  content: T[]
  totalElements: number
  number: number
  size: number
}

// ── expenses ───────────────────────────────────────────────────────────────

export const listExpenses = (p: {
  from?: string; to?: string; category?: string; paymentMethod?: string
  eventId?: string; unitId?: string; page?: number; size?: number
} = {}) => api.get<Paged<Expense>>(`/finance/expenses${qs(p)}`)

export const getExpense = (id: string) => api.get<Expense>(`/finance/expenses/${id}`)

export const recordExpense = (body: {
  electionId?: string | null; eventId?: string | null; incurredOn: string
  categoryCode: string; purpose?: string | null; unitId?: string | null
  payee?: string | null
  /** Give the total, OR give quantity and unit price and the server multiplies them. */
  amount?: number | null
  quantity?: number | null; unitAmount?: number | null; unitLabel?: string | null
  currency?: string
  paymentMethodCode: string; paymentReference?: string | null
  description?: string | null; mobilizerId?: string | null
}) => api.post<Expense>('/finance/expenses', body)

/** Correct an expense by filing the entry that reverses it. Never an edit, never a delete. */
export const reverseExpense = (id: string, body: { reasonCode: string, note?: string | null }) =>
  api.post<Expense>(`/finance/expenses/${id}/reverse`, body)

export const listReceipts = (expenseId: string) =>
  api.get<Receipt[]>(`/finance/expenses/${expenseId}/receipts`)

export const attachReceipt = (expenseId: string, body: {
  documentNumber?: string | null; issuedOn?: string | null; supplier?: string | null
  amount?: number | null; currency?: string | null; storageObjectId?: string | null
  fileName?: string | null; contentType?: string | null
}) => api.post<Receipt>(`/finance/expenses/${expenseId}/receipts`, body)

// ── events ─────────────────────────────────────────────────────────────────

export const listEvents = (p: {
  from?: string; to?: string; status?: string; eventType?: string
  unitId?: string; page?: number; size?: number
} = {}) => api.get<Paged<CampaignEvent>>(`/finance/events${qs(p)}`)

export const getEvent = (id: string) => api.get<CampaignEvent>(`/finance/events/${id}`)

export const saveEvent = (body: {
  electionId?: string | null; title: string; eventTypeCode: string; statusCode?: string | null
  startsAt: string; endsAt?: string | null; unitId?: string | null; venue?: string | null
  expectedAttendance?: number | null; actualAttendance?: number | null
  attendanceBasisCode?: string | null; notes?: string | null
}, id?: string) => id
  ? api.put<CampaignEvent>(`/finance/events/${id}`, body)
  : api.post<CampaignEvent>('/finance/events', body)

export const listAttendees = (eventId: string) =>
  api.get<Attendee[]>(`/finance/events/${eventId}/attendees`)

export const addAttendee = (eventId: string, body: {
  voterId?: string | null; mobilizerId?: string | null; userId?: string | null
  fullName: string; roleAtEvent?: string | null; organisation?: string | null
  phoneMask?: string | null; attended?: boolean; note?: string | null
}) => api.post<Attendee>(`/finance/events/${eventId}/attendees`, body)

export const removeAttendee = (eventId: string, attendeeId: string) =>
  api.del<void>(`/finance/events/${eventId}/attendees/${attendeeId}`)

// ── analysis & vocabulary ──────────────────────────────────────────────────

export const spendAnalysis = (p: {
  from?: string; to?: string; unitId?: string; electionId?: string
} = {}) => api.get<SpendAnalysis>(`/finance/analysis${qs(p)}`)

export interface PricePoint {
  on: string
  unitAmount: number
  quantity: number | null
  payee: string | null
}

/** What one thing has cost over time — the question a total cannot answer. */
export interface ItemPrice {
  key: string
  label: string
  /** Null when the item was bought in more than one currency; every figure is then null. */
  currency: string | null
  firstUnit: number | null
  latestUnit: number | null
  changePct: number | null
  cheapestPaid: number | null
  cheapestPayee: string | null
  dearestPaid: number | null
  dearestPayee: string | null
  totalQuantity: number | null
  totalSpend: number | null
  purchases: number
  firstOn: string
  latestOn: string
  history: PricePoint[]
}

export const itemPrices = (p: { from?: string, to?: string, unitId?: string } = {}) =>
  api.get<ItemPrice[]>(`/finance/item-prices${qs(p)}`)

export interface StatementLine {
  reference: string
  incurredOn: string
  payee: string | null
  purpose: string | null
  unitName: string | null
  amount: number
  paymentMethodCode: string
  paymentReference: string | null
  reversal: boolean
  reversesReference: string | null
  receipts: number
}

export interface StatementHeading {
  categoryCode: string
  label: string
  amount: number | null
  entries: number
  share: number | null
  lines: StatementLine[]
}

/**
 * A statement of EXPENDITURE. Not the full return the Act asks for — contributions
 * received are not held here, and `contributionsNote` says so on the face of it.
 */
export interface ExpenditureStatement {
  electionId: string | null
  electionCode: string | null
  electionName: string | null
  from: string
  to: string
  currency: string | null
  mixedCurrencyReason: string | null
  gross: number | null
  reversed: number | null
  net: number | null
  entries: number
  reversals: number
  withReceipt: number
  withoutReceipt: number
  withoutReceiptAmount: number | null
  headings: StatementHeading[]
  contributionsNote: string
  preparedAt: string
  preparedBy: string | null
}

export const expenditureStatement = (p: {
  electionId?: string, from?: string, to?: string, unitId?: string
} = {}) => api.get<ExpenditureStatement>(`/finance/statement${qs(p)}`)

export const exportStatement = (p: {
  electionId?: string, from?: string, to?: string, unitId?: string
} = {}) => api.post<{ fileId: string, filename: string, sizeBytes: number, entries: number }>(
  `/finance/statement/export${qs(p)}`, {})

export const expenseCategories = () =>
  api.get<Vocab[]>('/finance/vocabulary/expense-categories')
export const paymentMethods = () => api.get<Vocab[]>('/finance/vocabulary/payment-methods')
export const reversalReasons = () => api.get<Vocab[]>('/finance/vocabulary/reversal-reasons')
export const eventTypes = () => api.get<Vocab[]>('/finance/vocabulary/event-types')
export const eventStatuses = () => api.get<Vocab[]>('/finance/vocabulary/event-statuses')
export const attendanceBases = () => api.get<Vocab[]>('/finance/vocabulary/attendance-bases')
